import {
  canAccess,
  requirePermission,
  useAccess,
} from '@/features/auth/access';
import { isUuidV7 } from '@nest/ids';
import { t } from '@/lib/i18n';
import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  PageContainer,
  ProCard,
  ProDescriptions,
} from '@ant-design/pro-components';
import { Button, Result, Space, Tag, Tooltip } from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { AsyncBoundary } from '@/components/async-boundary';
import { useDictType } from '@/features/dict/dict-api';
import { DictItemFormModal } from '@/features/dict/item/form-modal';
import { DictItemTable } from '@/features/dict/item/table';
import { DictOptionsPreview } from '@/features/dict/option/preview';
import { DictTypeFormModal } from '@/features/dict/type/form-modal';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { formatDateTime, fromNow } from '@/lib/format';

/**
 * 一个字典，连同它的字典项。
 *
 * 一个页面管两件事（字典本身的信息 + 它的项），因为它们是同一个东西的两半：字典项离开类型没有
 * 意义，而类型离开项也只是一个名字。路径里的 id 不是UUID v7时不必发请求 —— API 会回 400，而
 * 事实是「没有这个页面」。
 */
export const Route = createFileRoute('/_authenticated/system/dicts/$typeId')({
  beforeLoad: ({ context, location }) =>
    requirePermission(context.queryClient, location.href, 'dicts:read'),
  component: DictTypeDetailPage,
});

function DictTypeDetailPage() {
  const writable = canAccess(useAccess().data, 'dicts:write');
  const { typeId } = Route.useParams();
  const navigate = useNavigate();
  const isValidId = isUuidV7(typeId);

  const dictType = useDictType(typeId, {
    enabled: isValidId,
  });
  const [createItemOpen, setCreateItemOpen] = useState(false);

  useDocumentTitle(isValidId ? t('字典 #{{id}}', { id: typeId }) : t('字典'));

  if (!isValidId) {
    return (
      <Result
        status="404"
        title={t('字典不存在')}
        subTitle={t('“{{id}}”不是一个合法的字典编号。', { id: typeId })}
        extra={
          <Link to="/system/dicts">
            <Button type="primary">{t('返回字典列表')}</Button>
          </Link>
        }
      />
    );
  }

  return (
    <PageContainer
      header={{
        title: dictType.data?.name ?? t('字典'),
        onBack: () => void navigate({ to: '/system/dicts' }),
        extra: [
          <Link key="list" to="/system/dicts">
            <Button icon={<ArrowLeftOutlined />}>{t('返回列表')}</Button>
          </Link>,
        ],
      }}
    >
      <AsyncBoundary result={dictType} skeletonRows={6}>
        {(data) => (
          <Space orientation="vertical" size={16} className="flex">
            <ProCard
              title={t('字典信息')}
              variant="outlined"
              extra={
                writable ? <EditDictTypeAction dictType={data} /> : undefined
              }
            >
              <ProDescriptions
                column={{ xs: 1, sm: 2, lg: 3 }}
                classNames={{ content: 'min-w-0 break-all' }}
                dataSource={data}
                columns={[
                  {
                    title: t('编号'),
                    dataIndex: 'id',
                    copyable: true,
                    className: 'break-all',
                  },
                  { title: t('字典名称'), dataIndex: 'name' },
                  {
                    title: t('字典编码'),
                    dataIndex: 'type',
                    copyable: true,
                    render: (_, record) => (
                      <Tag className="max-w-full whitespace-normal break-all">
                        {record.type}
                      </Tag>
                    ),
                  },
                  {
                    title: t('状态'),
                    dataIndex: 'isActive',
                    render: (_, record) =>
                      record.isActive ? (
                        <Tag color="success">{t('启用')}</Tag>
                      ) : (
                        <Tag color="default">{t('已停用')}</Tag>
                      ),
                  },
                  {
                    title: t('字典项'),
                    dataIndex: 'items',
                    render: (_, record) =>
                      t('{{count}} 项', { count: record.items.length }),
                  },
                  {
                    title: t('创建时间'),
                    dataIndex: 'createdAt',
                    render: (_, record) => (
                      <Tooltip title={formatDateTime(record.createdAt)}>
                        <span>{fromNow(record.createdAt)}</span>
                      </Tooltip>
                    ),
                  },
                  {
                    title: t('更新时间'),
                    dataIndex: 'updatedAt',
                    render: (_, record) => (
                      <Tooltip title={formatDateTime(record.updatedAt)}>
                        <span>{fromNow(record.updatedAt)}</span>
                      </Tooltip>
                    ),
                  },
                  {
                    title: t('备注'),
                    dataIndex: 'remark',
                    span: 'filled',
                    render: (_, record) => record.remark ?? '—',
                  },
                ]}
              />
            </ProCard>

            <ProCard
              title={t('字典项')}
              variant="outlined"
              extra={
                writable ? (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setCreateItemOpen(true)}
                  >
                    {t('新建字典项')}
                  </Button>
                ) : undefined
              }
            >
              <DictItemTable
                writable={writable}
                typeId={data.id}
                items={data.items}
                loading={dictType.isFetching}
                onReload={() => void dictType.refetch()}
              />
            </ProCard>

            <ProCard title={t('对外查询')} variant="outlined">
              {/* 类型停用时不去问接口：它会回答空列表，而原因上面已经写着「已停用」。 */}
              <DictOptionsPreview type={data.type} enabled={data.isActive} />
            </ProCard>

            {writable && createItemOpen ? (
              <DictItemFormModal
                typeId={data.id}
                onClose={() => setCreateItemOpen(false)}
              />
            ) : null}
          </Space>
        )}
      </AsyncBoundary>
    </PageContainer>
  );
}

/**
 * 编辑字典本身（名称、状态、备注）。
 *
 * 单独一个小节点是为了让「编辑」这个动作留在它打开的那个卡片上 —— 页面不需要知道这个弹窗的
 * 开关状态，它只属于这一个按钮。
 */
const EditDictTypeAction = ({
  dictType,
}: {
  readonly dictType: {
    readonly id: string;
    readonly name: string;
    readonly type: string;
    readonly isActive: boolean;
    readonly remark: string | null;
  };
}) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button icon={<EditOutlined />} onClick={() => setOpen(true)}>
        {t('编辑')}
      </Button>
      {open ? (
        <DictTypeFormModal dictType={dictType} onClose={() => setOpen(false)} />
      ) : null}
    </>
  );
};
